import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Nishab (minimal untuk wajib zakat)
const NISHAB = {
  emas: 85, // gram
  gaji: 2757000 // Rp (setara 85 gram emas)
};

function ZakatCalculator() {
  const navigate = useNavigate();
  const [zakatType, setZakatType] = useState('penghasilan');
  const [inputs, setInputs] = useState({
    penghasilan: { bulanan: '' },
    emas: { berat: '', hargaPerGram: '' },
    tabungan: { jumlah: '', bulanBekerja: '' },
    nilaiBisnis: { nilai: '', hutang: '' },
    pertanian: { hasil: '', tipeHasil: 'tanpa-irigasi' }
  });

  const handleInputChange = (type, field, value) => {
    setInputs(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value ? parseFloat(value) : ''
      }
    }));
  };

  const results = useMemo(() => {
    let hasil = {
      nishab: 0,
      total: 0,
      zakat: 0,
      persentase: 0,
      keterangan: '',
      wajibZakat: false
    };

    switch (zakatType) {
      case 'penghasilan': {
        const { bulanan } = inputs.penghasilan;
        if (!bulanan) return hasil;

        hasil.nishab = NISHAB.gaji;
        hasil.total = bulanan;
        hasil.wajibZakat = bulanan >= NISHAB.gaji;

        if (hasil.wajibZakat) {
          hasil.zakat = bulanan * 0.025; // 2.5%
          hasil.persentase = 2.5;
          hasil.keterangan = `Gaji bulanan Anda sebesar Rp ${bulanan.toLocaleString('id-ID')} telah mencapai atau melebihi nishab (Rp ${NISHAB.gaji.toLocaleString('id-ID')}). Kewajiban zakat sebesar 2,5%.`;
        } else {
          hasil.keterangan = `Gaji bulanan Anda masih di bawah nishab (Rp ${NISHAB.gaji.toLocaleString('id-ID')}). Zakat penghasilan belum wajib, namun infaq sunnah sangat dianjurkan.`;
        }
        break;
      }

      case 'emas': {
        const { berat, hargaPerGram } = inputs.emas;
        if (!berat || !hargaPerGram) return hasil;

        const totalNilai = berat * hargaPerGram;
        hasil.nishab = NISHAB.emas * hargaPerGram;
        hasil.total = totalNilai;
        hasil.wajibZakat = berat >= NISHAB.emas;

        if (hasil.wajibZakat) {
          hasil.zakat = totalNilai * 0.025;
          hasil.persentase = 2.5;
          hasil.keterangan = `Emas simpanan seberat ${berat} gram (estimasi nilai Rp ${totalNilai.toLocaleString('id-ID')}) telah memenuhi nishab 85 gram. Zakat wajib dikeluarkan sebesar 2,5%.`;
        } else {
          hasil.keterangan = `Emas simpanan Anda (${berat} gram) belum mencapai nishab minimal 85 gram. Zakat emas belum wajib.`;
        }
        break;
      }

      case 'tabungan': {
        const { jumlah, bulanBekerja } = inputs.tabungan;
        if (!jumlah || !bulanBekerja) return hasil;

        const rataRataBulanan = jumlah / bulanBekerja;
        hasil.nishab = NISHAB.gaji;
        hasil.total = jumlah;
        hasil.wajibZakat = rataRataBulanan >= NISHAB.gaji;

        if (hasil.wajibZakat) {
          hasil.zakat = jumlah * 0.025;
          hasil.persentase = 2.5;
          hasil.keterangan = `Tabungan sebesar Rp ${jumlah.toLocaleString('id-ID')} dengan rata-rata bulanan Rp ${rataRataBulanan.toLocaleString('id-ID', {maximumFractionDigits: 0})} telah memenuhi nishab. Zakat wajib sebesar 2,5%.`;
        } else {
          hasil.keterangan = `Rata-rata tabungan bulanan Anda masih di bawah nishab (Rp ${NISHAB.gaji.toLocaleString('id-ID')}). Zakat belum wajib.`;
        }
        break;
      }

      case 'nilaiBisnis': {
        const { nilai, hutang } = inputs.nilaiBisnis;
        if (nilai === '' && hutang === '') return hasil;

        const hutangAmount = hutang || 0;
        const nilaiAfterHutang = nilai - hutangAmount;
        hasil.nishab = NISHAB.gaji;
        hasil.total = nilaiAfterHutang;
        hasil.wajibZakat = nilaiAfterHutang >= NISHAB.gaji;

        if (hasil.wajibZakat) {
          hasil.zakat = nilaiAfterHutang * 0.025;
          hasil.persentase = 2.5;
          hasil.keterangan = `Nilai bersih bisnis sebesar Rp ${nilaiAfterHutang.toLocaleString('id-ID')} telah memenuhi nishab. Zakat perdagangan wajib sebesar 2,5%.`;
        } else {
          hasil.keterangan = `Nilai bersih bisnis Anda belum mencapai nishab. Zakat belum wajib.`;
        }
        break;
      }

      case 'pertanian': {
        const { hasil: hasilPanen, tipeHasil } = inputs.pertanian;
        if (!hasilPanen) return hasil;

        const nishabPertanian = 652.8;
        hasil.nishab = nishabPertanian;
        hasil.total = hasilPanen;
        hasil.wajibZakat = hasilPanen >= nishabPertanian;

        if (hasil.wajibZakat) {
          const persentaseZakat = tipeHasil === 'dengan-irigasi' ? 5 : 10;
          hasil.zakat = hasilPanen * (persentaseZakat / 100);
          hasil.persentase = persentaseZakat;
          hasil.keterangan = `Hasil panen sebesar ${hasilPanen} kg telah memenuhi nishab 652,8 kg. Kadar zakat adalah ${persentaseZakat}%.`;
        } else {
          hasil.keterangan = `Hasil panen (${hasilPanen} kg) belum mencapai nishab minimal 652,8 kg. Zakat belum wajib.`;
        }
        break;
      }

      default:
        break;
    }

    return hasil;
  }, [zakatType, inputs]);

  const isCalculated = results.total > 0;

  const zakatCategories = [
    { value: 'penghasilan', label: 'Zakat Penghasilan' },
    { value: 'emas', label: 'Emas & Perak' },
    { value: 'tabungan', label: 'Tabungan' },
    { value: 'nilaiBisnis', label: 'Perdagangan / Bisnis' },
    { value: 'pertanian', label: 'Pertanian' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-blue-900 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Header Section */}
        <div className="bg-white rounded-2xl border border-blue-100 p-6 md:p-8 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1 flex-1">
            <span className="inline-block text-[11px] font-semibold tracking-wider text-blue-700 uppercase bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
              Simulasi Syariat MPZ DT
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-blue-900 tracking-tight mt-1">
              Kalkulator Zakat
            </h1>
            <p className="text-xs md:text-sm text-slate-600">
              Hitung kewajiban zakat Anda secara akurat sesuai ketentuan nishab dan syariat Islam.
            </p>
          </div>
        </div>

        {/* Grid Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Kolom Kiri: Tab Pilihan & Formulir Input (7 Cols) */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Pilihan Jenis Zakat */}
            <div className="bg-white rounded-2xl border border-blue-100 p-6 shadow-sm space-y-4">
              <h2 className="text-xs font-bold text-blue-900 uppercase tracking-wider">
                Pilih Jenis Zakat
              </h2>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {zakatCategories.map(type => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setZakatType(type.value);
                      setInputs(prev => ({
                        ...prev,
                        [type.value]: Object.fromEntries(
                          Object.entries(prev[type.value]).map(([k]) => [k, ''])
                        )
                      }));
                    }}
                    className={`px-4 py-3 rounded-xl border text-xs font-semibold transition-all text-center ${
                      zakatType === type.value
                        ? 'border-blue-900 bg-blue-900 text-white shadow-sm'
                        : 'border-blue-100 bg-slate-50/60 text-slate-700 hover:border-blue-300 hover:bg-white'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Formulir Input */}
            <div className="bg-white rounded-2xl border border-blue-100 p-6 shadow-sm space-y-4">
              <h2 className="text-xs font-bold text-blue-900 uppercase tracking-wider">
                Masukkan Data Penghasilan / Harta
              </h2>

              {zakatType === 'penghasilan' && (
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-700">
                    Gaji / Penghasilan Bersih Bulanan (Rp)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-semibold text-xs">Rp</span>
                    <input
                      type="number"
                      value={inputs.penghasilan.bulanan}
                      onChange={(e) => handleInputChange('penghasilan', 'bulanan', e.target.value)}
                      placeholder="Contoh: 5000000"
                      className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/50 text-blue-900 font-semibold focus:bg-white focus:border-blue-600 outline-none text-xs"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Ketentuan Nishab: <span className="font-semibold text-blue-900">Rp {NISHAB.gaji.toLocaleString('id-ID')} / bulan</span> (Setara 85 gram emas per tahun)
                  </p>
                </div>
              )}

              {zakatType === 'emas' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">Berat Emas (Gram)</label>
                    <input
                      type="number"
                      value={inputs.emas.berat}
                      onChange={(e) => handleInputChange('emas', 'berat', e.target.value)}
                      placeholder="Contoh: 100"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/50 text-blue-900 font-semibold focus:bg-white focus:border-blue-600 outline-none text-xs"
                    />
                    <p className="text-[11px] text-slate-500">Ketentuan Nishab Emas: <span className="font-semibold text-blue-900">{NISHAB.emas} Gram</span></p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">Harga Emas per Gram Saat Ini (Rp)</label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 font-semibold text-xs">Rp</span>
                      <input
                        type="number"
                        value={inputs.emas.hargaPerGram}
                        onChange={(e) => handleInputChange('emas', 'hargaPerGram', e.target.value)}
                        placeholder="Contoh: 1100000"
                        className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/50 text-blue-900 font-semibold focus:bg-white focus:border-blue-600 outline-none text-xs"
                      />
                    </div>
                  </div>
                </div>
              )}

              {zakatType === 'tabungan' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">Total Saldo Tabungan (Rp)</label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 font-semibold text-xs">Rp</span>
                      <input
                        type="number"
                        value={inputs.tabungan.jumlah}
                        onChange={(e) => handleInputChange('tabungan', 'jumlah', e.target.value)}
                        placeholder="Contoh: 50000000"
                        className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/50 text-blue-900 font-semibold focus:bg-white focus:border-blue-600 outline-none text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">Lama Menabung (Bulan)</label>
                    <input
                      type="number"
                      value={inputs.tabungan.bulanBekerja}
                      onChange={(e) => handleInputChange('tabungan', 'bulanBekerja', e.target.value)}
                      placeholder="Contoh: 12"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/50 text-blue-900 font-semibold focus:bg-white focus:border-blue-600 outline-none text-xs"
                    />
                  </div>
                </div>
              )}

              {zakatType === 'nilaiBisnis' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">Nilai Aset Usaha / Perdagangan (Rp)</label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 font-semibold text-xs">Rp</span>
                      <input
                        type="number"
                        value={inputs.nilaiBisnis.nilai}
                        onChange={(e) => handleInputChange('nilaiBisnis', 'nilai', e.target.value)}
                        placeholder="Contoh: 100000000"
                        className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/50 text-blue-900 font-semibold focus:bg-white focus:border-blue-600 outline-none text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">Hutang Jatuh Tempo Bisnis (Rp) <span className="text-slate-400 text-[10px] font-normal">(Opsional)</span></label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 font-semibold text-xs">Rp</span>
                      <input
                        type="number"
                        value={inputs.nilaiBisnis.hutang}
                        onChange={(e) => handleInputChange('nilaiBisnis', 'hutang', e.target.value)}
                        placeholder="Contoh: 20000000"
                        className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/50 text-blue-900 font-semibold focus:bg-white focus:border-blue-600 outline-none text-xs"
                      />
                    </div>
                  </div>
                </div>
              )}

              {zakatType === 'pertanian' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">Hasil Panen Bersih (Kg)</label>
                    <input
                      type="number"
                      value={inputs.pertanian.hasil}
                      onChange={(e) => handleInputChange('pertanian', 'hasil', e.target.value)}
                      placeholder="Contoh: 1000"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/50 text-blue-900 font-semibold focus:bg-white focus:border-blue-600 outline-none text-xs"
                    />
                    <p className="text-[11px] text-slate-500">Nishab Pertanian: <span className="font-semibold text-blue-900">652,8 Kg</span></p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700">Metode Irigasi</label>
                    <select
                      value={inputs.pertanian.tipeHasil}
                      onChange={(e) => handleInputChange('pertanian', 'tipeHasil', e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/50 text-blue-900 font-semibold focus:bg-white focus:border-blue-600 outline-none text-xs"
                    >
                      <option value="tanpa-irigasi">Tanpa Irigasi Berbayar / Tadah Hujan (Zakat 10%)</option>
                      <option value="dengan-irigasi">Dengan Irigasi Berbayar (Zakat 5%)</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Kolom Kanan: Hasil Kalkulasi (5 Cols) */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Summary Card */}
            <div className="bg-white rounded-2xl border border-blue-100 p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-blue-50">
                <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900">Hasil Kalkulasi</h3>
                {isCalculated && (
                  <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${
                    results.wajibZakat
                      ? 'bg-amber-50 text-amber-900 border-amber-200'
                      : 'bg-slate-100 text-slate-700 border-slate-200'
                  }`}>
                    {results.wajibZakat ? 'Wajib Zakat' : 'Belum Wajib'}
                  </span>
                )}
              </div>

              {!isCalculated ? (
                <div className="py-8 text-center space-y-1">
                  <p className="text-xs font-semibold text-slate-700">Formulir Belum Diisi</p>
                  <p className="text-[11px] text-slate-500 max-w-xs mx-auto">
                    Ketik nominal harta pada formulir di samping untuk melihat rincian kalkulasi zakat.
                  </p>
                </div>
              ) : (
                <div className="space-y-4 pt-1">
                  <div>
                    <p className="text-[10px] font-semibold uppercase text-slate-400">Total Harta / Subtotal</p>
                    <p className="text-xl font-bold text-blue-900 mt-0.5">
                      {zakatType === 'pertanian' 
                        ? `${results.total.toFixed(2)} Kg`
                        : `Rp ${results.total.toLocaleString('id-ID', {maximumFractionDigits: 0})}`
                      }
                    </p>
                  </div>

                  {results.wajibZakat ? (
                    <div className="bg-blue-50/70 rounded-xl p-4 border border-blue-100 space-y-2">
                      <p className="text-[10px] font-bold uppercase text-blue-700">Kewajiban Zakat ({results.persentase}%)</p>
                      <p className="text-2xl font-extrabold text-blue-900">
                        {zakatType === 'pertanian'
                          ? `${results.zakat.toFixed(2)} Kg`
                          : `Rp ${results.zakat.toLocaleString('id-ID', {maximumFractionDigits: 0})}`
                        }
                      </p>
                      <button
                        onClick={() => navigate('/donasi/zakat')}
                        className="w-full mt-3 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-blue-950 text-xs font-bold shadow-sm transition"
                      >
                        Tunaikan Zakat Sekarang
                      </button>
                    </div>
                  ) : (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-2">
                      <p className="text-xs font-semibold text-slate-800">Belum Wajib Zakat</p>
                      <p className="text-[11px] text-slate-600 leading-relaxed">
                        Harta Anda belum mencapai nishab. Namun infaq & sedekah sunnah tetap dianjurkan.
                      </p>
                      <button
                        onClick={() => navigate('/donasi/infaq-sedekah')}
                        className="w-full mt-2 py-2 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-xs font-bold shadow-sm transition"
                      >
                        Salurkan Infaq & Sedekah
                      </button>
                    </div>
                  )}

                  <div className="bg-slate-50/60 rounded-xl p-3 border border-slate-200/60 text-[11px] text-slate-600 leading-relaxed">
                    {results.keterangan}
                  </div>
                </div>
              )}
            </div>

            {/* Fiqh Guidelines Card */}
            <div className="bg-white rounded-2xl border border-blue-100 p-6 shadow-sm space-y-2.5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900">Catatan Ringkas Fiqh Zakat</h3>
              <ul className="space-y-1.5 text-xs text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-blue-900 font-bold">•</span>
                  <span>Zakat ditunaikan saat harta memenuhi syarat nishab dan kepemilikan haul (1 tahun).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-900 font-bold">•</span>
                  <span>Penghasilan profesi dapat dikeluarkan setiap kali menerima gaji atau diakumulasi tahunan.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-900 font-bold">•</span>
                  <span>Disalurkan secara transparan kepada 8 golongan asnaf penerima zakat via MPZ DT.</span>
                </li>
              </ul>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}

export default ZakatCalculator;
